import { useEffect, useRef } from 'react';
import { UnstyledButton } from '@mantine/core';
import { BotAvatar } from './BotAvatar/BotAvatar';
import type { BotAvatarHandle } from './BotAvatar/BotAvatar.types';
import './ChatAIAvatar.scss';

interface ChatAIAvatarProps {
    paused?: boolean;
    compact?: boolean;
    working?: boolean;
    inMessage?: boolean;
    error?: boolean;
    completedReplyId?: string;
}

export function ChatAIAvatar({
    paused = false,
    compact = false,
    working = false,
    inMessage = false,
    error = false,
    completedReplyId,
}: ChatAIAvatarProps) {
    const botRef = useRef<BotAvatarHandle>(null);
    const seenReplyId = useRef(completedReplyId);
    const small = compact || inMessage;
    const className = `chat-ai-avatar${small ? ' chat-ai-avatar--compact' : ''}${working ? ' chat-ai-avatar--working' : ''}${error ? ' chat-ai-avatar--error' : ''}`;

    useEffect(() => {
        if (working) return;
        if (completedReplyId && completedReplyId !== seenReplyId.current) botRef.current?.jump();
        seenReplyId.current = completedReplyId;
    }, [completedReplyId, working]);

    // Своего состояния «работает» у движка нет: спокойное ожидание — в CSS.
    const avatar = (
        <BotAvatar
            ref={botRef}
            size="var(--chat-ai-avatar-size)"
            // Опущенная голова и прикрытые глаза; короткое покачивание задаёт CSS.
            state={error ? 'sleeping' : 'default'}
            speed={error ? 0.4 : working || inMessage ? 0.5 : 0.8}
            turn={working || inMessage ? 0.25 : 0.65}
            jumpSpin={small ? 0 : 1}
            paused={paused}
            followPointer={!inMessage && !error}
        />
    );

    if (small) {
        return (
            <span className={className} data-paused={paused} aria-hidden="true">
                {avatar}
            </span>
        );
    }

    return (
        <UnstyledButton
            type="button"
            className={className}
            data-paused={paused}
            aria-label="Поприветствовать помощника"
            onClick={() => botRef.current?.jump()}
        >
            {avatar}
        </UnstyledButton>
    );
}
